import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, MapPin, ArrowRight, Boxes, Layers, FolderOpen, Archive, ArchiveRestore, FolderInput, Trash2, RotateCcw, Image as ImageIcon, CheckCircle2 } from 'lucide-react';
import { useProjectsHubStore, isArchived, isTrashed, TRASH_DAYS } from '../../stores/useProjectsHubStore';
import { StatusPill, Eyebrow, STAGE_LABEL, fmtDate, fmtMoney, daysSince } from './projectUi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;
const mono = { fontFamily: 'JetBrains Mono, ui-monospace, monospace' };

const Hero = ({ hero, project }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [hero?.render_id]);
  const previews = (project?.lots_preview || []).slice(0, 4);
  return (
    <div className="p-3 bg-[var(--c-surface-2)] border-b border-[var(--c-border)]">
      <div className="relative aspect-video rounded-xl overflow-hidden bg-[var(--c-surface-0)] border border-[var(--c-border)] shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="recap-hero">
        {hero && !broken ? (
          <img src={`${BASE}${hero.url}`} alt={hero.label || 'Rendu'} className="w-full h-full object-cover" crossOrigin="use-credentials" onError={() => setBroken(true)} data-testid="recap-hero-image" />
        ) : previews.length ? (
          <div className={`w-full h-full grid gap-0.5 ${previews.length === 1 ? 'grid-cols-1' : previews.length === 2 ? 'grid-cols-2' : 'grid-cols-2 grid-rows-2'}`}>
            {previews.map((lp) => <img key={lp.lot_id} src={`${API}/files/${lp.file_id}/download`} alt={lp.name} className="w-full h-full object-cover" crossOrigin="use-credentials" />)}
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-[var(--c-text-faint)]" data-testid="recap-hero-placeholder">
            <ImageIcon className="w-7 h-7" strokeWidth={1.25} />
            <span className="text-[11px] uppercase tracking-[0.18em]">Aucun rendu pour l'instant</span>
          </div>
        )}
        {hero && !broken && (
          <span className="absolute left-3 bottom-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/90 backdrop-blur text-[10.5px] font-semibold text-[#18181B] shadow-sm" data-testid="recap-hero-badge">
            {hero.validated && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
            {STAGE_LABEL[hero.stage] || hero.stage}{hero.validated ? ' · validé' : ''}
          </span>
        )}
      </div>
    </div>
  );
};

const Milestone = ({ label, date, last }) => (
  <div className="flex-1 min-w-0 relative">
    <div className="flex items-center">
      <span className={`w-2 h-2 rounded-full shrink-0 ${date ? 'bg-[var(--c-gold)]' : 'bg-[var(--c-surface-3)] border border-[var(--c-border-strong)]'}`} />
      {!last && <span className="flex-1 h-px bg-[var(--c-border)]" />}
    </div>
    <p className="mt-2 text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-faint)] truncate">{label}</p>
    <p className="text-[12.5px] text-[var(--c-text)] tabular-nums" style={mono}>{date ? fmtDate(date) : '—'}</p>
  </div>
);

const Metric = ({ label, value, testid }) => (
  <div className="rounded-xl border border-[var(--c-border)] bg-[var(--c-surface-0)] px-3.5 py-3">
    <p className="text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-faint)] truncate">{label}</p>
    <p className="mt-1 text-[18px] font-semibold text-[var(--c-text)] tabular-nums leading-none" style={mono} data-testid={testid}>{value}</p>
  </div>
);

export const ProjectRecapPanel = ({ project, onMove, onAct }) => {
  const navigate = useNavigate();
  const closePanel = useProjectsHubStore((s) => s.closePanel);
  const folders = useProjectsHubStore((s) => s.folders);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const id = project?.project_id;

  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    setData(null); setErr(false);
    axios.get(`${API}/projects/${id}/overview`, { withCredentials: true })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [id]);

  if (!project) return null;
  const m = data?.metrics || {};
  const steps = data?.progress?.steps || [];
  const pct = data?.progress?.percent ?? 0;
  const trashed = isTrashed(project);
  const archived = isArchived(project);
  const folder = folders.find((f) => f.folder_id === project.folder_id);
  const go = (section) => navigate(`/projects/${id}${section ? `?section=${section}` : ''}`);

  return (
    <aside className="flex flex-col h-full bg-[var(--c-surface-0)] border-l border-[var(--c-border)]" data-testid="project-recap-panel" style={{ boxShadow: '-12px 0 32px rgba(0,0,0,0.04)' }}>
      <div className="flex items-center justify-between px-4 h-12 border-b border-[var(--c-border)] shrink-0">
        <Eyebrow>Récapitulatif</Eyebrow>
        <button type="button" onClick={closePanel} className="w-8 h-8 rounded-md flex items-center justify-center text-[var(--c-text-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] transition-colors" data-testid="recap-close-btn" aria-label="Fermer">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <Hero hero={data?.hero} project={project} />

        <div className="px-5 pt-5 pb-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-[20px] font-semibold tracking-tight text-[var(--c-text)] leading-tight truncate" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="recap-title">{project.name}</h2>
              <p className="mt-1 flex items-center gap-1.5 text-[12.5px] text-[var(--c-text-2)] min-w-0">
                <MapPin className="w-3.5 h-3.5 shrink-0 text-[var(--c-text-faint)]" /><span className="truncate">{project.address || 'Adresse non renseignée'}</span>
              </p>
            </div>
            <StatusPill status={project.status} testid="recap-status" />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-[var(--c-text-muted)]">
            {folder && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[var(--c-border)]"><FolderOpen className="w-3 h-3" />{folder.name}</span>}
            {archived && <span className="px-2 py-0.5 rounded-md border border-[var(--c-border)] uppercase tracking-wider">Archivé</span>}
            {trashed && <span className="px-2 py-0.5 rounded-md border border-red-200 bg-red-50 text-red-600" data-testid="recap-trash-note">Corbeille · purge dans {Math.max(0, TRASH_DAYS - daysSince(project.deleted_at))} j</span>}
            {m.opportunities_amount > 0 && <span className="px-2 py-0.5 rounded-md bg-[var(--c-gold-soft)] text-[var(--c-gold)] font-semibold">Pipeline {fmtMoney(m.opportunities_amount)}</span>}
          </div>
          {project.description && <p className="text-[13px] text-[var(--c-text-2)] leading-relaxed line-clamp-3">{project.description}</p>}
        </div>

        <div className="px-5 py-4 border-t border-[var(--c-border)]">
          <Eyebrow className="mb-3">Jalons</Eyebrow>
          <div className="flex gap-2" data-testid="recap-timeline">
            <Milestone label="Création" date={project.created_at} />
            <Milestone label="Commercialisation" date={project.general_info?.date_commercialisation} />
            <Milestone label="Livraison" date={project.general_info?.date_livraison} last />
          </div>
          <p className="mt-3 text-[11px] text-[var(--c-text-faint)]">Dernière modification le {fmtDate(project.updated_at, { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>

        <div className="px-5 py-4 border-t border-[var(--c-border)]">
          <div className="flex items-center justify-between mb-3">
            <Eyebrow>Avancement maquette</Eyebrow>
            <span className="text-[13px] font-semibold tabular-nums text-[var(--c-text)]" style={mono} data-testid="recap-progress-percent">{pct}%</span>
          </div>
          {err ? <p className="text-[12px] text-[var(--c-text-muted)]">Avancement indisponible.</p> : !data ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-8 rounded-lg bg-[var(--c-surface-2)] animate-pulse" />)}</div>
          ) : steps.length === 0 ? (
            <p className="text-[12px] text-[var(--c-text-muted)]">Aucune maquette lancée — commencez par l'onglet Maquette.</p>
          ) : (
            <div className="space-y-2.5" data-testid="recap-progress-steps">
              {steps.map((st, i) => {
                const r = st.total ? Math.round((100 * st.done) / st.total) : 0;
                return (
                  <div key={st.key}>
                    <div className="flex items-center justify-between text-[12px]">
                      <span className="text-[var(--c-text-2)]"><span className="text-[var(--c-text-faint)] mr-1.5" style={mono}>{i + 1}</span>{st.label}</span>
                      <span className="tabular-nums text-[var(--c-text-muted)]" style={mono}>{st.done}/{st.total}</span>
                    </div>
                    <div className="mt-1 h-1 rounded-full bg-[var(--c-surface-3)] overflow-hidden"><div className={`h-full rounded-full ${r === 100 ? 'bg-emerald-500' : 'bg-[var(--c-gold)]'}`} style={{ width: `${r}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[var(--c-border)]">
          <Eyebrow className="mb-3">Chiffres</Eyebrow>
          <div className="grid grid-cols-3 gap-2" data-testid="recap-metrics">
            <Metric label="Lots" value={m.lots ?? project.lots_count ?? 0} testid="recap-metric-lots" />
            <Metric label="Bâtiments" value={m.buildings ?? '—'} testid="recap-metric-buildings" />
            <Metric label="Fichiers" value={m.files ?? '—'} />
            <Metric label="Lots avec plan" value={m.lots_with_plan ?? '—'} />
            <Metric label="Lots générés" value={m.lots_with_gen ?? '—'} />
            <Metric label="Rendus validés" value={m.renders_total != null ? `${m.renders_validated}/${m.renders_total}` : '—'} />
          </div>
        </div>
      </div>

      <div className="px-5 py-4 border-t border-[var(--c-border)] bg-[var(--c-surface-1)] shrink-0 space-y-2.5">
        {trashed ? (
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => onAct([id], 'restore')} className="hud-btn hud-btn-primary hud-btn-md" data-testid="recap-restore-btn"><RotateCcw className="w-4 h-4" />Restaurer</button>
            <button type="button" onClick={() => onAct([id], 'delete')} className="hud-btn hud-btn-danger hud-btn-md" data-testid="recap-delete-forever-btn"><Trash2 className="w-4 h-4" />Supprimer</button>
          </div>
        ) : (
          <>
            <button type="button" onClick={() => go()} className="hud-btn hud-btn-primary hud-btn-md w-full" data-testid="recap-open-project-btn">Ouvrir le projet<ArrowRight className="w-4 h-4" /></button>
            <div className="grid grid-cols-3 gap-2">
              <button type="button" onClick={() => go('production&tab=preparation')} className="hud-btn hud-btn-secondary hud-btn-sm" data-testid="recap-open-maquette-btn"><Boxes className="w-3.5 h-3.5" />Préparation</button>
              <button type="button" onClick={() => go('production&tab=generation')} className="hud-btn hud-btn-secondary hud-btn-sm" data-testid="recap-open-production-btn"><Layers className="w-3.5 h-3.5" />Génération</button>
              <button type="button" onClick={() => go('ressources&tab=fichiers')} className="hud-btn hud-btn-secondary hud-btn-sm" data-testid="recap-open-files-btn"><FolderOpen className="w-3.5 h-3.5" />Fichiers</button>
            </div>
            <div className="flex items-center justify-center gap-4 pt-1 text-[11.5px]">
              <button type="button" onClick={() => onMove([id])} className="inline-flex items-center gap-1 text-[var(--c-text-muted)] hover:text-[var(--c-gold)] transition-colors" data-testid="recap-move-btn"><FolderInput className="w-3.5 h-3.5" />Déplacer</button>
              <button type="button" onClick={() => onAct([id], archived ? 'unarchive' : 'archive')} className="inline-flex items-center gap-1 text-[var(--c-text-muted)] hover:text-[var(--c-gold)] transition-colors" data-testid="recap-archive-btn">
                {archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}{archived ? 'Désarchiver' : 'Archiver'}
              </button>
              <button type="button" onClick={() => onAct([id], 'trash')} className="inline-flex items-center gap-1 text-[var(--c-text-muted)] hover:text-red-500 transition-colors" data-testid="recap-trash-btn"><Trash2 className="w-3.5 h-3.5" />Corbeille</button>
            </div>
          </>
        )}
      </div>
    </aside>
  );
};
